import * as XLSX from "xlsx";

import type { BulkAdjustmentRawRow } from "~/types/stockAdjustment";

export interface BulkParseResult {
  rows: BulkAdjustmentRawRow[];
  warnings: string[];
  fatal?: string;
}

/** Parse a Chilean/US formatted number ("1.234,56", "1,234.56", "-10", "1234.56").
 * Mirrors parseLocaleNumber in pages/costing/components/CosteoShared — kept local so
 * this parser has no page-layer dependency. */
function parseNumber(raw: string): number | null {
  const s = raw.trim().replace(/\$/g, "").replace(/\s/g, "");
  if (!s) return null;
  let normalized = s;
  if (s.includes(",") && s.includes(".")) {
    normalized =
      s.lastIndexOf(",") > s.lastIndexOf(".")
        ? s.replace(/\./g, "").replace(",", ".") // 1.234,56
        : s.replace(/,/g, ""); // 1,234.56
  } else if (s.includes(",")) {
    normalized = s.replace(",", ".");
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

const findCol = (headers: string[], test: (h: string) => boolean) =>
  headers.findIndex((h) => test(String(h ?? "")));

/**
 * Parse the "Ajuste Masivo" upload (the seeded catálogo template or any file with
 * the same columns: Tipo · Código · Ítem · Unidad · Bodega/Planta · Cantidad ·
 * Costo · Lote · Notas). Header matching is tolerant and only the input columns are
 * read; reference columns are ignored. Rows with a blank/0 Cantidad are skipped, so
 * a full catálogo with a handful of quantities filled yields only those rows.
 */
export function parseBulkAdjustmentFile(
  data: string | ArrayBuffer
): BulkParseResult {
  const wb = XLSX.read(data, { type: "binary" });
  // Prefer the "Ajustes" sheet from the template; fall back to the first sheet.
  const sheetName =
    wb.SheetNames.find((n) => /ajuste/i.test(n)) ?? wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    blankrows: false,
  });

  let headerIdx = -1;
  for (let i = 0; i < Math.min(grid.length, 15); i++) {
    const h = (grid[i] ?? []).map((c) => String(c ?? ""));
    const hasTipo = h.some((x) => /tipo/i.test(x));
    const hasCode = h.some((x) => /c[oó]d/i.test(x));
    const hasQty = h.some((x) => /cantidad/i.test(x));
    if (hasTipo && hasCode && hasQty) {
      headerIdx = i;
      break;
    }
  }
  if (headerIdx === -1) {
    return {
      rows: [],
      warnings: [],
      fatal:
        "No se reconocieron las columnas. Usa el formato base (Tipo · Código · Bodega/Planta · Cantidad …).",
    };
  }

  const headers = (grid[headerIdx] as unknown[]).map((c) => String(c ?? ""));
  const tipoIdx = findCol(headers, (h) => /tipo/i.test(h));
  const codeIdx = findCol(headers, (h) => /c[oó]d/i.test(h));
  const locIdx = findCol(headers, (h) => /bodega|planta|ubicaci/i.test(h));
  const qtyIdx = findCol(headers, (h) => /cantidad/i.test(h));
  const unitIdx = findCol(headers, (h) => /unidad/i.test(h) && !/costo/i.test(h));
  const costIdx = findCol(headers, (h) => /costo/i.test(h));
  const lotIdx = findCol(headers, (h) => /lote/i.test(h));
  const notesIdx = findCol(headers, (h) => /nota/i.test(h));

  const rows: BulkAdjustmentRawRow[] = [];
  const warnings: string[] = [];
  const str = (r: unknown[], idx: number) =>
    idx >= 0 ? String(r[idx] ?? "").trim() : "";

  for (let i = headerIdx + 1; i < grid.length; i++) {
    const r = grid[i] as unknown[] | undefined;
    if (!r) continue;
    const codigo = str(r, codeIdx);
    const qtyStr = str(r, qtyIdx);
    if (!codigo && !qtyStr) continue; // fully blank row
    const quantity = qtyStr ? parseNumber(qtyStr) : null;
    // Skip rows the user left without a quantity — those are untouched catalog rows.
    if (quantity === null || quantity === 0) continue;
    if (!codigo) {
      if (warnings.length < 8)
        warnings.push(`Fila ${i + 1}: cantidad sin código`);
      continue;
    }
    rows.push({
      rowNumber: i + 1,
      tipoRaw: str(r, tipoIdx),
      codigo,
      location: str(r, locIdx),
      quantity,
      unit: str(r, unitIdx) || null,
      unitCost: costIdx >= 0 ? parseNumber(String(r[costIdx] ?? "")) : null,
      lot: str(r, lotIdx) || null,
      notes: str(r, notesIdx) || null,
    });
  }

  return { rows, warnings };
}
